import {
  AccountBookTwoTone,
  CalculatorOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons';
import { Alert, Form, Result } from 'antd';
import { FormInstance } from 'antd/es/form';
import BigNumber from 'bignumber.js';
import React from 'react';
import { APIError } from '../../../../api/types';
import NumericKeyboard from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import Price from '@/components/Price';
import { PayQRScanner, PayQRScannerProps } from '@/components/QRScanner';
import { isGrouponPaymentChannel, PaymentChannel } from '@/models/payment';
import { UserFullInfo } from '@/models/user';
import { playSound } from '@/utils/sound';
import BalanceConsume from '../BalanceConsume';
import CashCalc from '../CashCalc';
import PaymentChannelSelector from '../PaymentChannelSelector';
import styles from '../style.less';
import { IconDY, IconMT } from '@/components/Icons';

type Props = {
  form: FormInstance;
  user: UserFullInfo;
  requestLoading: boolean;
  requestError: APIError;
  balancePayAvailable?: boolean;
  // onRecharge: SettlementProps['onRecharge'];
  onRequestCodePayTradeCancel: PayQRScannerProps['onRequestCodePayTradeCancel'];
};

const Payment: React.FC<Props> = ({
  form,
  user,
  requestLoading,
  requestError,
  // onRecharge,
  balancePayAvailable = true,
  onRequestCodePayTradeCancel,
}: Props) => {
  return (
    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.actualPrice !== cur.actualPrice}>
      {({ getFieldValue }) => {
        const actualPrice = getFieldValue('actualPrice');
        const totalPrice = getFieldValue('totalPrice');
        const balancePrice = getFieldValue('balancePrice') || new BigNumber(0);

        if (actualPrice) {
          return (
            <>
              <div className={styles.payMethodSelectorContainer}>
                <h2>
                  <AccountBookTwoTone twoToneColor={'#eb2f96'} /> 待收款:{' '}
                  <Price value={new BigNumber(actualPrice).plus(balancePrice)} />
                </h2>

                {balancePayAvailable ? (
                  <Form.Item noStyle name={'balancePrice'}>
                    <BalanceConsume
                      user={user?.user}
                      actualPrice={actualPrice}
                      totalPrice={totalPrice}
                    />
                  </Form.Item>
                ) : null}

                <Form.Item noStyle name={'paymentChannel'}>
                  <PaymentChannelSelector
                    actualPrice={new BigNumber(actualPrice)}
                    balancePayAvailable={balancePayAvailable}
                    user={user ? user.user : null}
                    onChange={() => {
                      playSound('click');
                    }}
                  />
                </Form.Item>
              </div>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.paymentChannel !== cur.paymentChannel}>
                {({ getFieldValue }) => {
                  const paymentChannel = getFieldValue('paymentChannel');
                  if (paymentChannel === PaymentChannel.Online) {
                    return (
                      <Form.Item name={'authCode'} style={{ paddingTop: 20 }}>
                        <PayQRScanner
                          loading={requestLoading}
                          error={requestError}
                          onRequestCodePayTradeCancel={onRequestCodePayTradeCancel}
                          onChange={() => {
                            form.submit();
                          }}
                        />
                      </Form.Item>
                    );
                  }
                }}
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.paymentChannel !== cur.paymentChannel}>
                {({ getFieldValue }) => {
                  const paymentChannel = getFieldValue('paymentChannel');
                  if (paymentChannel === PaymentChannel.Cash) {
                    return (
                      <>
                        <h3 className={styles.calcHeader}>
                          <div>
                            <CalculatorOutlined /> 收款计算器
                          </div>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, cur) => prev.cashReceived !== cur.cashReceived}>
                            {({ getFieldValue }) => {
                              return (
                                <CashCalc
                                  actualPrice={new BigNumber(getFieldValue('actualPrice') || 0)}
                                  cashReceived={new BigNumber(getFieldValue('cashReceived') || 0)}
                                />
                              );
                            }}
                          </Form.Item>
                        </h3>
                        <Form.Item noStyle name={'cashReceived'}>
                          <NumericKeyboard
                            parseValueToNumber
                            precision={2}
                            className={styles.calcKeyboard}
                            placeholder={'请输入收款金额'}
                            onComplete={() => {
                              form.submit();
                            }}
                          />
                        </Form.Item>
                      </>
                    );
                  }
                }}
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.paymentChannel !== cur.paymentChannel}>
                {({ getFieldValue, setFieldsValue }) => {
                  const paymentChannel = getFieldValue('paymentChannel');
                  if (
                    paymentChannel === PaymentChannel.INVALID ||
                    isGrouponPaymentChannel(paymentChannel)
                  ) {
                    return (
                      <>
                        {isGrouponPaymentChannel(paymentChannel) && (
                          <div className={styles.grouponContainer}>
                            <Alert type="warning" showIcon message="请确保已经在对应平台完成验券" />
                            <div className={styles.grouponSelect}>
                              <div
                                className={`${styles.grouponItem} ${
                                  paymentChannel === PaymentChannel.Groupon_MT ? styles.active : ''
                                }`}
                                onClick={() => {
                                  playSound('click');
                                  setFieldsValue({ paymentChannel: PaymentChannel.Groupon_MT });
                                }}>
                                <span className={styles.grouponItemContent}>
                                  <IconMT /> 美团
                                </span>
                                <CheckCircleOutlined
                                  className={styles.grouponItemActiveIndicator}
                                />
                              </div>
                              <div
                                className={`${styles.grouponItem} ${
                                  paymentChannel === PaymentChannel.Groupon_DY ? styles.active : ''
                                }`}
                                onClick={() => {
                                  playSound('click');
                                  setFieldsValue({ paymentChannel: PaymentChannel.Groupon_DY });
                                }}>
                                <span className={styles.grouponItemContent}>
                                  <IconDY /> 抖音
                                </span>
                                <CheckCircleOutlined
                                  className={styles.grouponItemActiveIndicator}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <h3 className={styles.invalidPaymentHeader}>
                          <div>
                            <CalculatorOutlined /> 交易确认
                          </div>
                        </h3>
                        <div className="text-center">
                          <PosButton
                            type="primary"
                            onClick={() => {
                              form.submit();
                            }}>
                            本单无需额外收款，请核对无误后点此确认
                          </PosButton>
                        </div>
                      </>
                    );
                  }
                }}
              </Form.Item>
            </>
          );
        } else {
          return <Result icon={<DollarCircleOutlined />} title="请输入待收款金额" />;
        }
      }}
    </Form.Item>
  );
};

export default Payment;
